'use client'
import { useTranslate } from '@repo/shared/hooks'
import { globalConfig } from '@repo/shared/utils'
import { type UserProject, useGetProjectDetails } from '@repo/services'
import { useSearchParams } from 'next/navigation'
import type { ProjectsProject } from '@repo/shared/types'

export const useProject = (projectId?: string) => {
  const searchParams = useSearchParams()
  const tab = searchParams.get('tab')
  const { data: response, isLoading, error } = useGetProjectDetails(projectId)
  const { t } = useTranslate()

  const formatValue = (value: string | number | boolean | undefined) => {
    if (typeof value === 'boolean') {
      return value ? t('common_has') : t('common_has_not')
    }
    return value?.toString() || ''
  }

  const projectData = (response?.data?.project ?? {}) as UserProject & {
    totalSupply: number
  }

  // Map API response to match UI components' expected data structure
  const project: ProjectsProject | object = projectData.id
    ? {
        id: projectData.token || '',
        token: projectData.token || '',
        city: projectData.location?.city?.name || '',
        province: projectData.location?.province?.name || '',
        district: projectData.location?.district?.name || '',
        county: projectData.location?.county?.name || '',
        name: projectData.displayName || '',
        pricePerMeter: projectData.price?.priceRange24h?.last || 0,
        percentChange: projectData.price?.priceRange24h?.deltaPercent || 0,
        description: projectData.description || '',
        usage: t(`common_${projectData.type as keyof typeof t}`) || '',
        type: 'initial', // Default to initial since API doesn't provide this
        galleryImages:
          projectData.photos
            ?.sort((a, b) => {
              // If a is the default photo, it should come first
              if (a.id === projectData.defaultPhotoID) return -1
              // If b is the default photo, it should come first
              if (b.id === projectData.defaultPhotoID) return 1
              // Otherwise maintain original order
              return 0
            })
            .map((photo) => `${globalConfig.baseImageUrl}${photo.bigURL}`) ||
          [],
        about: t('projectsDetail_about'),
        specs:
          projectData.details?.map((detail) => ({
            label: detail.key || '',
            value: formatValue(detail.value),
            valueType: detail.valueType as
              | 'string'
              | 'number'
              | 'boolean'
              | 'select',
          })) || [],

        location: {
          lat: projectData.geoLocation?.latitude || 0,
          lng: projectData.geoLocation?.longitude || 0,
        },
        totalArea: {
          m2: projectData?.market?.totalSupply || 0,
          cm2: projectData?.market?.totalSupply || 0 * 10000,
        },
        offeredArea: {
          m2: projectData?.circulation?.otcCirculating || 0,
          cm2: projectData?.circulation?.otcCirculating || 0 * 10000,
        },
        purchasableArea: { m2: 0, cm2: 0 }, // TODO: Add from API if available
        priceChartData: [], // TODO: Add from API if available
        logoURL: projectData.logoURL,
        documents: projectData.documents || [],
        defaultPhotoID: projectData?.defaultPhotoID,
        createdAt: projectData?.createdAt,
        state: projectData?.state,
        tradeStatus: projectData?.tradeStatus,
        market: {
          totalSupply: projectData?.market?.totalSupply || 0,
          maxSupply: projectData?.market?.maxSupply || 0,
          minOrderSize: projectData?.market?.minOrderSize || 0,
          releaseDate: projectData?.market?.releaseDate,
          listingDate: projectData?.market?.listingDate,
        },
        circulation: {
          otcCirculating: projectData?.circulation?.otcCirculating || 0,
          otcBuy: projectData?.circulation?.otcBuy || 0,
          otcSell: projectData?.circulation?.otcSell || 0,
        },
        price: {
          volume24h: projectData?.price?.volume24h || 0,
          marketCap: projectData?.price?.marketCap || 0,
          fullyDilutedMarketCap: projectData?.price?.fullyDilutedMarketCap || 0,
          circulatingCap: projectData?.price?.circulatingCap || 0,
        },
        kind: projectData?.kind || '',
        scale: projectData?.scale || 1,
      }
    : {}

  return {
    rawProject: projectData,
    project,
    projectData,
    isLoading,
    error,
    tab,
  }
}
